/**
 * print/printGeometry — millimetre↔pixel conversions and rectangle
 * helpers. Pure functions; no React, no DOM, no side effects.
 *
 * Convention: every artifact composition lives in *millimetre
 * space*. Preview is rendered at a chosen CSS pixel density; export
 * is rendered at the format's `dpi`. The same composition code
 * therefore drives both surfaces without divergence.
 */

const MM_PER_INCH = 25.4;

export function mmToPx(mm, dpi) {
  return (mm * dpi) / MM_PER_INCH;
}
export function pxToMm(px, dpi) {
  return (px * MM_PER_INCH) / dpi;
}

/**
 * For a given PrintFormat, return the three concentric rectangles
 * the artifact composition cares about, all in mm:
 *   • bleedRect — what the press prints, then trims into
 *   • trimRect  — the final guest-held piece
 *   • safeRect  — the inward zone where critical content must live
 */
export function rects(format) {
  const [trimW, trimH] = format.trim_mm;
  const bleed = format.bleed_mm || 0;
  const safe  = format.safe_mm  || 0;
  // We work in a coordinate system where the bleed rectangle starts
  // at (0,0). The trim rectangle is inset by `bleed` on every edge.
  return {
    bleedRect: { x: 0, y: 0, w: trimW + 2 * bleed, h: trimH + 2 * bleed },
    trimRect:  { x: bleed, y: bleed, w: trimW, h: trimH },
    safeRect:  { x: bleed + safe, y: bleed + safe, w: trimW - 2 * safe, h: trimH - 2 * safe },
  };
}

/**
 * Choose a preview pixel-density that lands the artifact at the
 * given on-screen width without exceeding 96 DPI (so the preview
 * stays a "preview", never doubles as a rasterised export). The
 * caller hands us the max CSS width available; we return a `pxPerMm`
 * that keeps the bleed rectangle inside it.
 */
export function previewScale(format, maxCssWidthPx) {
  const bleedWidthMm = format.trim_mm[0] + 2 * (format.bleed_mm || 0);
  // Cap at 96 DPI ≈ 3.78 px/mm — preview-not-export.
  const cap = 96 / MM_PER_INCH;
  const fit = maxCssWidthPx / bleedWidthMm;
  return Math.min(fit, cap);
}
